import React, { useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { Check } from 'lucide-react';
import { SelectItem } from '../../boutique-classroom.types';
import { EVENT_GROUPS_SELECTED_SUFFIX, EVENT_GROUPS_SUMMARY_MAX_NAMES, SELECT_PLACEHOLDER } from '../../boutique-classroom.constants';

interface GroupMultiSelectProps {
	options: SelectItem[];
	selected: SelectItem[];
	onToggle: (option: SelectItem) => void;
	emptyText: string;
	placeholder?: string;
	className?: string;
	disabled?: boolean;
}

const ESCAPE_KEY = 'Escape';
const ENTER_KEY = 'Enter';
const SPACE_KEY = ' ';
const NAME_SEPARATOR = ', ';
const CHECK_ICON_SIZE = 12;

function isActivationKey(key: string): boolean {
	return key === ENTER_KEY || key === SPACE_KEY;
}

function getSummary(selected: SelectItem[]): string {
	if (selected.length <= EVENT_GROUPS_SUMMARY_MAX_NAMES) return selected.map(option => option.name).join(NAME_SEPARATOR);
	return `${selected.length} ${EVENT_GROUPS_SELECTED_SUFFIX}`;
}

export const GroupMultiSelect: React.FC<GroupMultiSelectProps> = ({ options, selected, onToggle, emptyText, placeholder = SELECT_PLACEHOLDER, className, disabled = false }) => {
	const [isOpen, setIsOpen] = useState(false);
	const rootRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!isOpen) return;
		const handleMouseDown = (event: MouseEvent) => {
			if (rootRef.current && !rootRef.current.contains(event.target as Node)) setIsOpen(false);
		};
		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === ESCAPE_KEY) setIsOpen(false);
		};
		document.addEventListener('mousedown', handleMouseDown);
		document.addEventListener('keydown', handleKeyDown);
		return () => {
			document.removeEventListener('mousedown', handleMouseDown);
			document.removeEventListener('keydown', handleKeyDown);
		};
	}, [isOpen]);

	const toggleOpen = useCallback(() => {
		if (!disabled) setIsOpen(prev => !prev);
	}, [disabled]);

	const handleTriggerKeyDown = useCallback(
		(event: React.KeyboardEvent<HTMLDivElement>) => {
			if (!isActivationKey(event.key)) return;
			event.preventDefault();
			toggleOpen();
		},
		[toggleOpen]
	);

	const isSelected = useCallback((option: SelectItem) => selected.some(item => item.id === option.id), [selected]);

	return (
		<div
			ref={rootRef}
			className={clsx('custom-dropdown custom-dropdown--full-width custom-dropdown--menu-top cem-multi', className)}>
			<div
				className={clsx('custom-dropdown__trigger', { 'custom-dropdown__trigger--open': isOpen, 'custom-dropdown__trigger--disabled': disabled })}
				role="button"
				tabIndex={disabled ? -1 : 0}
				aria-haspopup="listbox"
				aria-expanded={isOpen}
				onClick={toggleOpen}
				onKeyDown={handleTriggerKeyDown}>
				<span className={clsx('custom-dropdown__label', { 'custom-dropdown__label--placeholder': selected.length === 0 })}>{selected.length ? getSummary(selected) : placeholder}</span>
				<i className={clsx('custom-dropdown__icon icons-alt-arrow-down', { 'custom-dropdown__icon--open': isOpen })} />
			</div>

			{isOpen && (
				<div
					className="custom-dropdown__menu"
					role="listbox"
					aria-multiselectable="true">
					{options.length === 0 ? (
						<div className="cem-multi__empty">{emptyText}</div>
					) : (
						options.map(option => {
							const checked = isSelected(option);
							return (
								<div
									key={option.id}
									className={clsx('custom-dropdown__option cem-multi__option', { 'custom-dropdown__option--selected': checked })}
									role="option"
									aria-selected={checked}
									tabIndex={0}
									onClick={() => onToggle(option)}
									onKeyDown={event => {
										if (!isActivationKey(event.key)) return;
										event.preventDefault();
										onToggle(option);
									}}>
									<span
										className={clsx('cem-multi__check', { 'cem-multi__check--checked': checked })}
										aria-hidden="true">
										{checked && <Check size={CHECK_ICON_SIZE} />}
									</span>
									<span className="cem-multi__name">{option.name}</span>
								</div>
							);
						})
					)}
				</div>
			)}
		</div>
	);
};

export default GroupMultiSelect;
